import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Plus } from 'lucide-react';

import noTasksPng from '@/assets/illustrations/empty/no-tasks.png';
import noSeasonsPng from '@/assets/illustrations/empty/no-seasons.png';

import { useTaskStore } from '@/stores/task-store';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import { useSeasonStore } from '@/stores/season-store';
import { usePrepActiveSeasonStore } from '@/stores/prep-active-season-store';
import { pickDefaultSeason } from '@/utils/pick-default-season';
import { PrepSeasonSelector } from '@/components/prep/PrepSeasonSelector';
import type { Task, TaskCategory } from '@/types/task';
import { getCategoryDisplayName } from '@/utils/category-utils';
import { getTaskStatus } from '@/utils/task-display-utils';
import {
  filterOpenTasksForPrep,
  countPrepFilters,
  sortPrep,
  groupByCategory,
} from '@/utils/prep-filter-utils';
import type { PrepFilter } from '@/utils/prep-filter-utils';
import { PrepHeader } from '@/components/tasks/PrepHeader';
import { PrepFilterBar } from '@/components/tasks/PrepFilterBar';
import { PrepCompletedToggle } from '@/components/tasks/PrepCompletedToggle';
import { TaskRow } from '@/components/tasks/TaskRow';

function weaponLabel(weaponType: string): string {
  const map: Record<string, string> = {
    gun: 'GUN',
    bow: 'BOW',
    muzzleloader: 'ML',
    crossbow: 'XB',
    youth: 'YOUTH',
    any: 'ANY',
    other: 'OTHER',
  };
  return map[weaponType] ?? weaponType.toUpperCase();
}

export function TaskListPage() {
  const { id: paramSeasonId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // Route /prep/tasks?season=<id> carries the id in a query param;
  // route /seasons/:id/tasks carries it as a URL param. Support both.
  const urlSeasonId = paramSeasonId ?? searchParams.get('season') ?? undefined;
  const navigate = useNavigate();
  const { tasks, isLoading: tasksLoading, loadTasks, completeTask, uncompleteTask } = useTaskStore();
  const { getSeason, seasons, seasonsLoaded, isLoading: seasonsLoading, loadSeasons } = useSeasonStore();
  const { seasonId: storedSeasonId, setSeason: setStoredSeason } = usePrepActiveSeasonStore();

  const [activeFilter, setActiveFilter] = useState<PrepFilter>('all');

  const today = useMemo(() => new Date(), []);
  const todayStr = today.toISOString().slice(0, 10);

  // Guard: seed the stored season only on first mount. After that, only explicit
  // user taps (via onSelect → setStoredSeason) should update the season.
  const hasSeededRef = useRef(false);

  useEffect(() => {
    if (seasons.length === 0) void loadSeasons();
  }, [seasons.length, loadSeasons]);

  // Seed the active season exactly once on first mount after seasons are loaded.
  // URL param wins if present; otherwise run the default-selection ladder.
  // Never re-seeds on subsequent renders — prevents reverting user's selector tap.
  useEffect(() => {
    if (hasSeededRef.current) return;
    if (!seasonsLoaded || seasons.length === 0) return;

    hasSeededRef.current = true;
    const seedId = urlSeasonId ?? pickDefaultSeason(seasons, today)?.id ?? null;
    if (seedId && !storedSeasonId) {
      setStoredSeason(seedId);
    } else if (urlSeasonId && urlSeasonId !== storedSeasonId) {
      // URL param takes precedence over whatever is stored, but only on first load.
      setStoredSeason(urlSeasonId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonsLoaded, seasons.length]);

  // Effective season: after first-mount seed, the store is the source of truth.
  // Before seeding, fall back to urlSeasonId so the page doesn't briefly show "no seasons".
  const effectiveSeasonId = storedSeasonId ?? urlSeasonId ?? undefined;

  useEffect(() => {
    if (effectiveSeasonId) void loadTasks(effectiveSeasonId);
  }, [effectiveSeasonId, loadTasks]);

  const season = effectiveSeasonId ? getSeason(effectiveSeasonId) : undefined;

  // Build title map for predecessor names
  const taskTitleMap = useMemo(
    () => new Map(tasks.map((t) => [t.id, t.title])),
    [tasks],
  );

  // Separate done tasks from open tasks
  const doneTasks = useMemo(
    () => tasks.filter((t) => getTaskStatus(t, todayStr) === 'done'),
    [tasks, todayStr],
  );

  // Filter counts for the badge pills
  const filterCounts = useMemo(
    () => countPrepFilters(tasks, today),
    [tasks, today],
  );

  // Open tasks matching the active filter, then sorted and grouped by category
  const filteredOpen = useMemo(
    () => filterOpenTasksForPrep(tasks, activeFilter, today),
    [tasks, activeFilter, today],
  );

  const groupedOpen = useMemo(
    () => groupByCategory(sortPrep(filteredOpen)),
    [filteredOpen],
  );

  const completedCount = doneTasks.length;
  const totalCount = tasks.length;

  async function handleToggleComplete(task: Task) {
    if (task.completed) {
      await uncompleteTask(task.id);
    } else {
      await completeTask(task.id);
    }
  }

  // While the season store is hydrating, show a skeleton rather than the "not found" error.
  if (!seasonsLoaded || (seasonsLoading && !season)) {
    return (
      <div className="flex flex-col gap-4 animate-pulse py-6 px-4">
        <div className="h-7 bg-[var(--color-elevated)] rounded w-48" />
        <div className="h-2 bg-[var(--color-elevated)] rounded w-full" />
        <div className="h-10 bg-[var(--color-elevated)] rounded w-full" />
        <div className="h-10 bg-[var(--color-elevated)] rounded w-full" />
      </div>
    );
  }

  // Seasons loaded but none exist — user hasn't created a season yet.
  if (!effectiveSeasonId) {
    return (
      <div data-testid="no-seasons-state">
        <OperationalGuide
          illustration={noSeasonsPng}
          heading="Set Up Your Season"
          subtext="Configure your season dates to align with state DNR regulations."
          ctaLabel="Add Season"
          ctaLink="/seasons/new"
          data-guide-id="task-list-no-seasons"
        />
      </div>
    );
  }

  // Explicit season ID given but not found in the store.
  if (!season && !tasksLoading) {
    return (
      <div className="flex flex-col gap-4 py-8">
        <p className="text-[var(--color-text-muted)] text-body-sm">Season not found.</p>
        <Link to="/seasons" className="text-body-sm text-[var(--color-accent)] hover:underline">
          Back to Seasons
        </Link>
      </div>
    );
  }

  const hasAnyOpen = filterCounts.all > 0;
  const hasFilteredResults = filteredOpen.length > 0;

  return (
    <div className="flex flex-col gap-0" data-testid="task-list-page">
      {/* Sticky instrument header */}
      <PrepHeader
        seasonName={season?.name ?? 'Tasks'}
        totalCount={totalCount}
        completedCount={completedCount}
        weaponLabel={season ? weaponLabel(season.weaponType) : ''}
        onBack={() => navigate(`/seasons/${effectiveSeasonId}`)}
      />

      {/* Season selector — always visible below the header */}
      <div
        className="px-4 py-2 border-b border-[var(--color-border)]/40"
        data-testid="prep-season-selector-row"
      >
        <PrepSeasonSelector
          seasons={seasons}
          selectedSeasonId={effectiveSeasonId ?? null}
          onSelect={(id) => {
            setStoredSeason(id);
          }}
        />
      </div>

      {/* Filter bar */}
      <PrepFilterBar
        activeFilter={activeFilter}
        counts={filterCounts}
        onChange={setActiveFilter}
      />

      {/* Task list */}
      {tasksLoading ? (
        <div className="px-4 py-8 text-center text-[var(--color-text-muted)] text-body-sm">
          Loading tasks…
        </div>
      ) : totalCount === 0 ? (
        <OperationalGuide
          illustration={noTasksPng}
          heading="Your Task List is Clear"
          subtext="Generate a checklist from a template to begin your seasonal prep."
          ctaLabel="Browse Templates"
          ctaLink="/templates"
          data-guide-id="task-list-empty"
        />
      ) : !hasAnyOpen && activeFilter === 'all' ? (
        <div
          className="flex flex-col items-center justify-center py-10 px-5 text-center"
          data-testid="tasks-all-empty"
          style={{ padding: '40px 20px' }}
        >
          <p className="font-display text-[18px] text-[var(--color-text-display)]">Nothing here</p>
          <p className="text-body-sm text-[var(--color-text-muted)] mt-1">No open tasks.</p>
        </div>
      ) : !hasFilteredResults ? (
        <div
          className="flex flex-col items-center justify-center text-center"
          data-testid="tasks-filtered-empty"
          style={{ padding: '40px 20px' }}
        >
          <p className="font-display text-[18px] text-[var(--color-text-display)]">Nothing here</p>
          <p className="text-body-sm text-[var(--color-text-muted)] mt-1">No tasks match this filter.</p>
        </div>
      ) : (
        <div className="bg-[var(--color-surface)]">
          {Array.from(groupedOpen.entries()).map(([category, categoryTasks]) => {
            // Category-level progress: done/total across all tasks in this category (not filtered)
            const allInCategory = tasks.filter((t) => t.category === category);
            const doneInCategory = allInCategory.filter((t) => t.completed).length;
            const totalInCategory = allInCategory.length;

            return (
              <div key={category} data-testid={`category-group-${category as string}`}>
                {/* Category header */}
                <div
                  className="flex items-baseline justify-between"
                  style={{ padding: '18px 16px 8px' }}
                >
                  <span
                    className="field-label"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {getCategoryDisplayName(category as TaskCategory)}
                  </span>
                  <span
                    style={{
                      fontSize: '10px',
                      color: 'var(--color-text-muted)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {doneInCategory}/{totalInCategory}
                  </span>
                </div>

                {/* Task rows */}
                {categoryTasks.map((task) => {
                  const predecessorTitles = (task.dependsOn ?? [])
                    .map((depId) => taskTitleMap.get(depId))
                    .filter((title): title is string => !!title);

                  return (
                    <TaskRow
                      key={task.id}
                      task={task}
                      seasonId={effectiveSeasonId}
                      predecessorTitles={predecessorTitles}
                      onToggleComplete={handleToggleComplete}
                      showStrikethrough={false}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Completed footer toggle */}
      <PrepCompletedToggle
        doneTasks={doneTasks}
        seasonId={effectiveSeasonId}
        onToggleComplete={handleToggleComplete}
      />

      {/* FAB — only rendered when there are tasks (empty-state CTA is the primary action) */}
      {tasks.length > 0 && (
        <button
          type="button"
          onClick={() => navigate(`/prep/tasks/new${effectiveSeasonId ? `?season=${effectiveSeasonId}` : ''}`)}
          aria-label="Create new task"
          data-testid="fab-create-task"
          className="fixed bottom-20 right-4 sm:bottom-8 w-14 h-14 rounded-full bg-[var(--color-accent)] text-[var(--color-text-display)] shadow-ember flex items-center justify-center hover:bg-[var(--color-accent-hover)] active:bg-[var(--color-accent-pressed)] transition-colors duration-100 z-20"
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
}
